import { FunctionComponent, useState } from 'react'
import { Box, Button, CircularProgress } from '@material-ui/core'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { makeStyles } from '@material-ui/styles'
import colors from '../../colors'
import { SPACING } from '../../constants'

interface PaymentStepProps {
  /** Shown above the card field, e.g. "$109/mo". */
  priceLabel: string
  submitting: boolean
  onBack: () => void
  /** Receives the Stripe payment method id once the card is accepted. */
  onPaymentMethod: (paymentMethodId: string) => void
}

const PaymentStep: FunctionComponent<PaymentStepProps> = ({
  priceLabel,
  submitting,
  onBack,
  onPaymentMethod,
}) => {
  const styles = useStyles()
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string | null>(null)
  const [tokenizing, setTokenizing] = useState(false)

  const busy = tokenizing || submitting
  const stripeUnavailable = !process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY

  const handleSubmit = async () => {
    if (!stripe || !elements) return

    const card = elements.getElement(CardElement)
    if (!card) return

    setTokenizing(true)
    setError(null)

    // Tokenize here so the raw card never touches our servers; the backend
    // only ever sees the resulting payment method id.
    const result = await stripe.createPaymentMethod({ type: 'card', card })

    setTokenizing(false)

    if (result.error) {
      setError(result.error.message || 'That card could not be verified.')
      return
    }

    onPaymentMethod(result.paymentMethod.id)
  }

  return (
    <Box>
      <Box className={styles.summary}>
        <span className={styles.summaryLabel}>Stitch Pro</span>
        <span className={styles.summaryPrice}>{priceLabel}</span>
      </Box>

      {stripeUnavailable && (
        <p className={styles.error}>
          Card entry is unavailable: REACT_APP_STRIPE_PUBLISHABLE_KEY was not set
          when this bundle was built. It must be present at build time — in
          .env.development for `npm start`, or .env / .env.production for
          `npm run build`.
        </p>
      )}

      <Box className={styles.cardBox}>
        <CardElement
          options={{
            hidePostalCode: false,
            style: {
              base: {
                fontSize: '16px',
                color: colors.STITCH_BLACK,
                '::placeholder': { color: colors.GREY_3 },
              },
              invalid: { color: colors.RED },
            },
          }}
          onChange={(e) => setError(e.error ? e.error.message : null)}
        />
      </Box>

      {error && <p className={styles.error}>{error}</p>}

      <p className={styles.reassurance}>
        Your card is charged when your account is created. Cancel any time.
      </p>

      <Button
        type="button"
        fullWidth
        disableElevation
        disabled={!stripe || busy}
        onClick={handleSubmit}
        sx={{
          mt: 2,
          py: 1.25,
          textTransform: 'none',
          fontSize: 15,
          fontWeight: 700,
          borderRadius: '8px',
        }}
      >
        {busy ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
        Create organization
      </Button>

      <Button
        type="button"
        fullWidth
        variant="text"
        disabled={busy}
        onClick={onBack}
        sx={{
          mt: 1.5,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: 15,
          color: colors.GREY_2,
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: colors.GREY_7 },
        }}
      >
        Back
      </Button>
    </Box>
  )
}

const useStyles = makeStyles({
  summary: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: `${SPACING * 2}px 0`,
    borderBottom: `1px solid ${colors.GREY_5}`,
    marginBottom: SPACING * 3,
  },
  summaryLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: colors.STITCH_BLACK,
  },
  summaryPrice: {
    fontSize: 15,
    color: colors.GREY_1,
  },
  cardBox: {
    border: `1px solid ${colors.GREY_4}`,
    borderRadius: 8,
    padding: SPACING * 1.75,
  },
  error: {
    margin: `${SPACING}px 0 0`,
    fontSize: 13,
    color: colors.RED,
  },
  reassurance: {
    margin: `${SPACING * 1.5}px 0 0`,
    fontSize: 12,
    color: colors.GREY_3,
  },
})

export default PaymentStep
